import { ArrowRightIcon } from "@heroicons/react/24/outline"
import { clsx } from "clsx"

import { Eyebrow, Panel } from "../BatteryOnlyProposal/primitives"

/**
 * The Solar V2 compute section's explanations (Hosting Agreement Rev 2.10,
 * Option B · B3): what a compute node is, and how the network bonus works in
 * plain words. Section-only copy. Plan figures arrive formatted from the
 * gateway's payout; without them the copy reads generically and states no
 * amount.
 */

export type ComputePlanFigures = {
  /** The plan's display name, e.g. "Max". */
  planName: string
  /** The guaranteed monthly base, formatted, e.g. "$400". */
  base: string
  /** The most the monthly bonus can be, formatted, e.g. "$400". */
  bonusCap: string
  /** The agreement's term in years, from the gateway's payout. */
  termYears: number
}

const FLOW = [
  {
    who: `Compute customers`,
    what: `AI companies, research labs and more pay Vendor for computing time`,
    arrow: `buy compute`,
  },
  {
    who: `Vendor`,
    what: `Sells the capacity and sends the work across its network of compute nodes`,
    arrow: `sends work`,
  },
  {
    who: `Your compute node`,
    what: `Runs the work on your home's power and internet`,
    arrow: `rewards you monthly`,
  },
] as const

export function ComputeNodeStory({
  figures,
}: {
  figures: ComputePlanFigures | null
}): JSX.Element {
  const answers = [
    {
      q: `Why`,
      h: `The world needs more computing power.`,
      p: `Demand for compute is outpacing supply. Hosting a node puts more of it to work and gets you paid for providing the home it runs in.`,
    },
    {
      q: `What it does`,
      h: `Sells computing time to Vendor's customers.`,
      p: `AI companies, research labs and other customers buy time on the network. You don't manage anything; Vendor finds the work and keeps the node running.`,
    },
    {
      q: `Who pays for it`,
      h: `Vendor owns it and pays for its upkeep.`,
      p: `No purchase, lease or loan. Vendor covers maintenance, repairs, upgrades and replacements ${figures ? `for ${figures.termYears} years` : `for the agreement's term`}. You provide the space, the electricity and a 1 Gbps internet connection.`,
    },
    {
      q: `How you earn`,
      h: `A fixed base, plus a reward when the network does well.`,
      p: figures
        ? `The ${figures.base} base is paid every month whether or not your node is busy. When the network earns a surplus, half of it is shared across hosts as a bonus, up to ${figures.bonusCap} a month on the ${figures.planName} plan.`
        : `The base is paid every month whether or not your node is busy. When the network earns a surplus, half of it is shared across hosts as a bonus, up to your plan's monthly limit.`,
    },
  ]

  return (
    <div data-testid="solar-ai-compute-story" className="mb-3.5">
      <Panel className="rounded-[20px] p-6 lg:p-8">
        <Eyebrow className="mb-2">What is a compute node?</Eyebrow>
        <h3 className="font-display max-w-[760px] text-[length:var(--step-2)] leading-tight font-semibold tracking-[-0.02em] text-bp-text">
          A supercomputer in your home, and you get rewarded for hosting it.
        </h3>
        <p className="mt-3 max-w-[860px] text-[14.5px] leading-relaxed text-bp-text-muted">
          Demand for computing power is growing faster than it can be built. Your
          Vendor compute node is a supercomputer that sells its computing time to
          customers like AI companies, research labs and more. Vendor owns it,
          finds the customers and pays you every month for hosting it.
        </p>

        <ol
          aria-label="How the money moves"
          className="mt-6 grid grid-cols-1 gap-2 lg:grid-cols-4"
        >
          {FLOW.map(({ who, what, arrow }) => (
            <li key={who} className="flex flex-col gap-2 lg:flex-row">
              <div className="flex-1 rounded-xl border border-bp-border bg-bp-elevated p-4">
                <div className="text-[13.5px] font-semibold text-bp-text">
                  {who}
                </div>
                <div className="mt-1 text-[12.5px] leading-snug text-bp-text-muted">
                  {what}
                </div>
              </div>
              <div className="flex items-center justify-center gap-1.5 text-[11px] text-bp-text-dim lg:w-16 lg:flex-col lg:gap-0.5 lg:text-center">
                <ArrowRightIcon
                  aria-hidden="true"
                  className="size-4 rotate-90 text-bp-accent lg:rotate-0"
                />
                <span>{arrow}</span>
              </div>
            </li>
          ))}
          <li className="rounded-xl border border-bp-success/40 bg-bp-success-soft p-4">
            <div className="text-[13.5px] font-semibold text-bp-text">You</div>
            <div className="mt-1 text-[12.5px] leading-snug text-bp-text-muted">
              {figures
                ? `${figures.base} fixed base every month, plus a share of network earnings up to ${figures.bonusCap}`
                : `A fixed base every month, plus a share of network earnings up to your plan's limit`}
            </div>
          </li>
        </ol>

        <div className="mt-6 grid grid-cols-1 gap-6 border-t border-bp-border pt-6 sm:grid-cols-2 lg:grid-cols-4">
          {answers.map(({ q, h, p }) => (
            <div key={q}>
              <Eyebrow>{q}</Eyebrow>
              <div className="mt-1.5 text-[15px] font-semibold text-bp-text">
                {h}
              </div>
              <p className="mt-1.5 text-[13px] leading-relaxed text-bp-text-muted">
                {p}
              </p>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  )
}

function MonthCard({
  tone,
  title,
  headline,
  body,
  base,
  bonus,
}: {
  tone: `slow` | `strong`
  title: string
  headline: string
  body: string
  base: string
  bonus: string
}): JSX.Element {
  return (
    <div
      className={clsx(
        `rounded-xl border p-5`,
        tone === `strong`
          ? `border-bp-success/40 bg-bp-success-soft`
          : `border-bp-border bg-bp-elevated`,
      )}
    >
      <Eyebrow>{title}</Eyebrow>
      <div className="mt-1.5 text-[16px] font-semibold text-bp-text">
        {headline}
      </div>
      <p className="mt-1.5 text-[13px] leading-relaxed text-bp-text-muted">
        {body}
      </p>
      <div className="mt-3.5 flex flex-wrap gap-x-3 gap-y-1 border-t border-bp-border pt-3 text-[13.5px] font-semibold tabular-nums">
        <span className="text-bp-text">{base}</span>
        <span
          className={
            tone === `strong` ? `text-bp-success` : `text-bp-text-muted`
          }
        >
          {bonus}
        </span>
      </div>
    </div>
  )
}

/** B3: the bonus as profit sharing, a strong month beside a slow one. No estimate beyond the cap. */
export function NetworkBonusPanel({
  figures,
}: {
  figures: ComputePlanFigures | null
}): JSX.Element {
  const base = figures ? `${figures.base} base` : `Your base`

  return (
    <div data-testid="solar-ai-compute-bonus" className="mb-3.5">
      <Panel className="rounded-[20px] p-6 lg:p-8">
        <Eyebrow className="mb-2">How the network bonus works</Eyebrow>
        <p className="max-w-[860px] text-[15px] leading-relaxed text-bp-text-soft">
          Think of the bonus as profit sharing. Your node is one of many in
          Vendor&apos;s network. When the network brings in more money than it
          costs to run, Vendor shares half of the extra with its hosts. Bigger
          plans get a bigger share, up to their plan&apos;s limit.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-3.5 md:grid-cols-2">
          <MonthCard
            tone="strong"
            title="A strong month"
            headline="The network earns more than it costs."
            body="Half of the extra goes to hosts, so you get your base plus a bonus."
            base={base}
            bonus={
              figures
                ? `+ bonus up to ${figures.bonusCap}`
                : `+ bonus up to your plan's limit`
            }
          />
          <MonthCard
            tone="slow"
            title="A slow month"
            headline="The network only covers its costs."
            body="There's nothing extra to share, so there's no bonus that month. Your base is still paid."
            base={base}
            bonus="+ $0 bonus"
          />
        </div>
        <ul className="mt-5 grid grid-cols-1 gap-4 text-[13px] leading-relaxed text-bp-text-muted md:grid-cols-3">
          <li>
            <span className="block font-semibold text-bp-text">
              It&apos;s about the whole network.
            </span>
            How busy your own node is doesn&apos;t change your bonus.
          </li>
          <li>
            <span className="block font-semibold text-bp-text">
              Slow months don&apos;t carry over.
            </span>
            A slow month never reduces a later one.
          </li>
          <li>
            <span className="block font-semibold text-bp-text">
              You can see the math.
            </span>
            Your monthly statement shows what the network earned, its costs and
            your share.
          </li>
        </ul>
      </Panel>
    </div>
  )
}
